import React from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { shallow } from "zustand/shallow";

import { getAverageScore, round } from "utils";
import useStore from "components/PerformanceReview/store";

import LineGraph, { LineGraphSkeleton } from "../../components/LineGraph";

/**
 * "Question specific performance" (mobile) — mobile equivalent of the left card
 * in desktop's `PerformanceReview/GraphAnalysis`.
 *
 * Plots each question's score (Overall / Speech / Content, chosen by the Score
 * Type select) across the attempt, exactly like desktop's per-question
 * PerformanceGraph. Questions come from the shared feedback store.
 *
 * @param {boolean} loading  feedback still loading
 */

// Same options as desktop's ScoreTypes (PerformanceReview/constants).
const SCORE_TYPES = [
  { _id: "overall", name: "Overall" },
  { _id: "communication", name: "Speech" },
  { _id: "content", name: "Content" },
];

export default function QuestionPerformance({ loading = false }) {
  const questions = useStore((s) => s.questions, shallow);
  const [scoreType, setScoreType] = React.useState("overall");

  const { labels, data } = React.useMemo(() => {
    const _labels = [];
    const _data = [];

    (questions || []).forEach((q, i) => {
      const communication = q?.communication?.ratings?.OVERALL || 0;
      const content = q?.content?.ratings?.OVERALL || 0;
      const byType = {
        overall: getAverageScore(communication, content),
        communication,
        content,
      };
      _labels.push(`Q${i + 1}`);
      _data.push(round(byType[scoreType] || 0));
    });

    return { labels: _labels, data: _data };
  }, [questions, scoreType]);

  return (
    <Box
      sx={{
        bgcolor: "shades.clr-white-900",
        border: "1px solid",
        borderColor: "neutral.clr-100",
        borderRadius: 3,
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Box>
        <Typography variant="h6-semiBold" color="neutral.clr-800">
          Question specific performance
        </Typography>
        <Typography
          variant="body02-regular"
          color="neutral.clr-500"
          sx={{ mt: 1, display: "block" }}
        >
          Performance trends in individual questions
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
        <Typography variant="body02-medium" color="neutral.clr-600">
          Score Type
        </Typography>
        <Select
          size="small"
          value={scoreType}
          onChange={(e) => setScoreType(e.target.value)}
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            minWidth: 130,
            borderRadius: 2,
            bgcolor: "shades.clr-white-900",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "neutral.clr-200" },
            "& .MuiSelect-select": { py: 1.5 },
          }}
        >
          {SCORE_TYPES.map((opt) => (
            <MenuItem key={opt._id} value={opt._id}>
              {opt.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {loading ? (
        <LineGraphSkeleton />
      ) : (
        <LineGraph labels={labels} data={data} xLabel="Questions" />
      )}
    </Box>
  );
}
